import { Component, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { DateTime } from 'luxon';
import {
  JackingFormInstanceEditor,
  JackingFinalValuesTemplateEditor,
  JackingFinalLegValuesEditor,
  JackingFinalLegValuesPerLegEditor,
  JackingFinalRpdEditor,
  JackingFinalRpdPerLegEditor,
  JackingFinalValuesEditor,
  JackingEventRowEditor,
} from '../../../../../../../models/jacking-v2/jacking.models';
import { JackingDateTimeEntryHelper } from '../../../../helpers/jacking-date-time-entry.helper';
import { DateService } from '../../../../../../../services/date/date.service';
import { PtLuxonDate } from '../../../../../../../models/projects/date.models';
import {JackingEventsTimeEngine} from '../../../../helpers/jacking-events-time-engine.helper';
import {ToastController} from 'ionic-angular';

type FinalTimeBlockKey =
  | 'finalLegValues'
  | 'finalPenetrations'
  | 'differenceFromExpected'
  | 'finalRpd'
  | 'finalAirGap';

interface FinalTimeBlock {
  timeUtc?: DateTime | PtLuxonDate | null;
  timeOffset?: number | null;
  timeConfirmed?: boolean;
}

interface FinalPenetrationsEditor extends FinalTimeBlock {
  id: number;
  legs: Array<{ id: number; legNumber: number; penetration?: number | null }>;
}

interface DifferenceFromExpectedEditor extends FinalTimeBlock {
  id: number;
  legs: Array<{ id: number; legNumber: number; difference?: number | null }>;
}

interface FinalAirGapEditor extends FinalTimeBlock {
  id: number;
  airGap?: number | null;
}

interface LiftPumpsUsedEditor {
  id: number;
  legs: Array<{ id: number; legNumber: number; liftPumpUsed: boolean }>;
  depthDeployed?: number | null;
}

@Component({
  selector: 'jacking-final-values-grid',
  templateUrl: 'jacking-final-values-grid.component.html'
})
export class JackingFinalValuesGridComponent implements OnInit, OnChanges {
  @Input() editor: JackingFormInstanceEditor;
  @Input() legsCount: number;
  @Input() orderedLegNumbers: number[] = [];
  @Input() isReadonly: boolean = false;
  @Input() activitySpansMultipleDays: boolean = false;
  @ViewChild('finalValuesScroll') finalValuesScrollRef: ElementRef<HTMLDivElement>;

  // text-mask pattern: HH:MM
  mask: any[] = [new RegExp('[0-2]'), new RegExp('\\d'), ':', new RegExp('[0-5]'), new RegExp('\\d')];

  private dtHelper: JackingDateTimeEntryHelper;
  private timeEngine: JackingEventsTimeEngine;

  // UI-only time state
  finalLegTimeToShow = '';
  finalLegSelectedLocalDateIso = '';

  finalPenTimeToShow = '';
  finalPenSelectedLocalDateIso = '';

  diffTimeToShow = '';
  diffSelectedLocalDateIso = '';

  finalRpdTimeToShow = '';
  finalRpdSelectedLocalDateIso = '';

  finalAirGapTimeToShow = '';
  finalAirGapSelectedLocalDateIso = '';

  // wrapper PtLuxonDate instances for the cell component inputs
  finalLegPt = new PtLuxonDate();
  finalPenPt = new PtLuxonDate();
  diffPt = new PtLuxonDate();
  finalRpdPt = new PtLuxonDate();
  finalAirGapPt = new PtLuxonDate();
  private lastFocusedRowIndex: number | null = null;
  private lastFocusedZone: 'item' | 'time' | 'leg' | 'other' = 'other';
  private tabNavigationPending = false;

  constructor(private dateService: DateService,
              private toastCtrl: ToastController,
              private elementRef: ElementRef) {
    this.dtHelper = new JackingDateTimeEntryHelper(this.dateService);
  }

  ngOnInit() {
    this.safeInit();

    this.timeEngine = new JackingEventsTimeEngine(this.dateService, this.toastCtrl, {
      activitySpansMultipleDays: this.activitySpansMultipleDays
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['editor']) {
      this.safeInit();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Skip internal time-cell controls and move straight into the row's value inputs.
    if (event.key === 'Tab' && !event.shiftKey && this.moveFromTimeInputToRowValues(event)) {
      return;
    }

    // Remember whether the next focus change came from keyboard tabbing.
    this.tabNavigationPending = event.key === 'Tab';

    if (event.key !== 'Tab' || event.shiftKey) {
      return;
    }

    const host = this.elementRef.nativeElement as HTMLElement;
    const activeElement = document.activeElement as HTMLElement | null;
    const tabbableElements = Array
      .from(host.querySelectorAll<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'));
    const firstCheckbox = host.querySelector('input[type="checkbox"]:not([disabled])') as HTMLInputElement | null;

    if (!activeElement || !firstCheckbox) {
      return;
    }

    const checkboxIndex = tabbableElements.indexOf(firstCheckbox);
    if (checkboxIndex <= 0 || tabbableElements[checkboxIndex - 1] !== activeElement) {
      return;
    }

    event.preventDefault();
    firstCheckbox.focus();
  }

  onGridFocusIn(event: FocusEvent): void {
    // Compare the previous and new focus zones so tabbing into the next row can reset horizontal scroll.
    const target = event.target as HTMLElement | null;
    if (!target) {
      this.tabNavigationPending = false;
      return;
    }

    const rowEl = target.closest('.final-row.body') as HTMLElement | null;
    const rowIndexText = rowEl?.getAttribute('data-row-index');
    const rowIndex = rowIndexText !== null ? Number(rowIndexText) : null;
    const zone = this.getFocusZone(target);

    if (
      this.tabNavigationPending &&
      rowIndex !== null &&
      rowIndex !== this.lastFocusedRowIndex &&
      this.lastFocusedZone === 'leg' &&
      (zone === 'item' || zone === 'time')
    ) {
      // When tabbing from right-side value cells into the next row, reveal the left columns again.
      this.resetHorizontalScroll();
    }

    this.lastFocusedRowIndex = rowIndex;
    this.lastFocusedZone = zone;
    this.tabNavigationPending = false;
  }

  toggleCheckboxFromEnter(event: KeyboardEvent) {
    event.preventDefault();
    const checkbox = event.target as HTMLInputElement | null;
    if (!checkbox || checkbox.disabled) {
      return;
    }

    checkbox.click();
  }

  // =====================================================================
  // TEMPLATE SWITCHES
  // =====================================================================

  get finalValuesTemplate(): JackingFinalValuesTemplateEditor | null {
    return this.editor?.template?.finalValuesTemplate || null;
  }

  get hasFinalLegValues(): boolean {
    return !!this.finalValuesTemplate?.hasFinalLegValues;
  }

  // =====================================================================
  // INSTANCE ACCESSORS
  // =====================================================================

  private get fv(): JackingFinalValuesEditor {
    return this.editor?.jackingFinalValues || ({} as JackingFinalValuesEditor);
  }

  get finalLegValues(): JackingFinalLegValuesEditor | null {
    return this.editor?.jackingFinalValues?.finalLegValues || null;
  }

  get finalPenetrations(): FinalPenetrationsEditor | null {
    return (this.editor?.jackingFinalValues as any)?.finalPenetrations || null;
  }

  get differenceFromExpected(): DifferenceFromExpectedEditor | null {
    return (this.editor?.jackingFinalValues as any)?.differenceFromExpected || null;
  }

  get finalRpd(): JackingFinalRpdEditor | null {
    return this.editor?.jackingFinalValues?.finalRpd || null;
  }

  get finalAirGap(): FinalAirGapEditor | null {
    return (this.editor?.jackingFinalValues as any)?.finalAirGap || null;
  }

  get liftPumpsUsed(): LiftPumpsUsedEditor | null {
    return (this.editor?.jackingFinalValues as any)?.liftPumpsUsed || null;
  }

  // =====================================================================
  // INIT + SHELLS
  // =====================================================================

  private safeInit() {
    if (!this.editor) {
      return;
    }

    // ensure root exists
    this.editor.jackingFinalValues = this.editor.jackingFinalValues || ({} as JackingFinalValuesEditor);

    // shells
    this.ensureFinalLegValuesShell();
    this.ensureFinalPenetrationsShell();
    this.ensureDifferenceFromExpectedShell();
    this.ensureFinalRpdShell();
    this.ensureFinalAirGapShell();
    this.ensureLiftPumpsUsedShell();

    // legs
    this.ensureFinalLegValuesLegRows();
    this.ensureFinalPenetrationsLegRows();
    this.ensureDifferenceLegRows();
    this.ensureRpdLegRows();
    this.ensureLiftPumpLegRows();

    // hydrate ui
    this.hydrateAllTimes();

    // keep pt wrappers in sync for the date-time cell component
    this.syncFinalPts();
  }

  private ensureFinalLegValuesShell() {
    if (!this.finalValuesTemplate?.hasFinalLegValues) { return; }

    const root = this.editor.jackingFinalValues;
    root.finalLegValues = root.finalLegValues || {
      timeUtc: null,
      timeOffset: null,
      timeConfirmed: false,
      legs: []
    };
    root.finalLegValues.legs = root.finalLegValues.legs || [];
  }

  private ensureFinalPenetrationsShell() {
    if (!this.finalValuesTemplate?.hasFinalPenetrations) { return; }

    const root = this.editor.jackingFinalValues as any;
    root.finalPenetrations = root.finalPenetrations || {
      id: 0,
      timeUtc: null,
      timeOffset: null,
      timeConfirmed: false,
      legs: []
    } as FinalPenetrationsEditor;
    root.finalPenetrations.legs = root.finalPenetrations.legs || [];
  }

  private ensureDifferenceFromExpectedShell() {
    if (!this.finalValuesTemplate?.hasDifferenceFromExpected) { return; }

    const root = this.editor.jackingFinalValues as any;
    root.differenceFromExpected = root.differenceFromExpected || {
      id: 0,
      timeUtc: null,
      timeOffset: null,
      timeConfirmed: false,
      legs: []
    } as DifferenceFromExpectedEditor;
    root.differenceFromExpected.legs = root.differenceFromExpected.legs || [];
  }

  private ensureFinalRpdShell() {
    if (!this.finalValuesTemplate?.hasRpd) { return; }

    const root = this.editor.jackingFinalValues;
    root.finalRpd = root.finalRpd || { id: 0, legs: [] } as JackingFinalRpdEditor;
    root.finalRpd.legs = root.finalRpd.legs || [];
  }

  private ensureFinalAirGapShell() {
    if (!this.finalValuesTemplate?.hasFinalAirGap) { return; }

    const root = this.editor.jackingFinalValues as any;
    root.finalAirGap = root.finalAirGap || {
      id: 0,
      airGap: null,
      timeUtc: null,
      timeOffset: null,
      timeConfirmed: false
    } as FinalAirGapEditor;
  }

  private ensureLiftPumpsUsedShell() {
    if (!this.finalValuesTemplate?.hasLiftPumpsUsed && !this.finalValuesTemplate?.hasDepthDeployed) { return; }

    const root = this.editor.jackingFinalValues as any;
    root.liftPumpsUsed = root.liftPumpsUsed || { id: 0, legs: [], depthDeployed: null } as LiftPumpsUsedEditor;
    root.liftPumpsUsed.legs = root.liftPumpsUsed.legs || [];
  }

  // =====================================================================
  // LEG ROW GETTERS + ENSURE
  // =====================================================================

  getFinalLegValuesLeg = (legNumber: number): JackingFinalLegValuesPerLegEditor => {
    const root = this.finalLegValues;
    if (!root) {
      return { id: 0, legNumber, load: null, ext: null } as any;
    }

    const existing = (root.legs || []).find(l => l.legNumber === legNumber);
    if (existing) { return existing; }

    const placeholder = { id: 0, legNumber, load: null, ext: null } as any;
    root.legs.push(placeholder);
    return placeholder;
  }

  getFinalPenetrationsLeg = (legNumber: number): { id: number; legNumber: number; penetration?: number | null } => {
    const root = this.finalPenetrations;
    if (!root) { return { id: 0, legNumber, penetration: null }; }

    const existing = (root.legs || []).find(l => l.legNumber === legNumber);
    if (existing) { return existing; }

    const placeholder = { id: 0, legNumber, penetration: null };
    root.legs.push(placeholder);
    return placeholder;
  }

  getDifferenceLeg = (legNumber: number): { id: number; legNumber: number; difference?: number | null } => {
    const root = this.differenceFromExpected;
    if (!root) { return { id: 0, legNumber, difference: null }; }

    const existing = (root.legs || []).find(l => l.legNumber === legNumber);
    if (existing) { return existing; }

    const placeholder = { id: 0, legNumber, difference: null };
    root.legs.push(placeholder);
    return placeholder;
  }

  getRpdLeg = (legNumber: number): JackingFinalRpdPerLegEditor => {
    const root = this.finalRpd;
    if (!root) { return { id: 0, legNumber, r: null, p: null, d: null }; }

    const existing = (root.legs || []).find(l => l.legNumber === legNumber);
    if (existing) { return existing; }

    const placeholder: JackingFinalRpdPerLegEditor = { id: 0, legNumber, r: null, p: null, d: null };
    root.legs.push(placeholder);
    return placeholder;
  }

  getLiftPumpLeg = (legNumber: number): { id: number; legNumber: number; liftPumpUsed: boolean } => {
    const root = this.liftPumpsUsed;
    if (!root) { return { id: 0, legNumber, liftPumpUsed: false }; }

    const existing = (root.legs || []).find(l => l.legNumber === legNumber);
    if (existing) {
      existing.liftPumpUsed = !!existing.liftPumpUsed;
      return existing;
    }

    const placeholder = { id: 0, legNumber, liftPumpUsed: false };
    root.legs.push(placeholder);
    return placeholder;
  }

  private ensureFinalLegValuesLegRows() {
    if (!this.finalValuesTemplate?.hasFinalLegValues || !this.finalLegValues) { return; }
    (this.orderedLegNumbers || []).forEach(n => this.getFinalLegValuesLeg(n));
  }

  private ensureFinalPenetrationsLegRows() {
    if (!this.finalValuesTemplate?.hasFinalPenetrations || !this.finalPenetrations) { return; }
    (this.orderedLegNumbers || []).forEach(n => this.getFinalPenetrationsLeg(n));
  }

  private ensureDifferenceLegRows() {
    if (!this.finalValuesTemplate?.hasDifferenceFromExpected || !this.differenceFromExpected) { return; }
    (this.orderedLegNumbers || []).forEach(n => this.getDifferenceLeg(n));
  }

  private ensureRpdLegRows() {
    if (!this.finalValuesTemplate?.hasRpd || !this.finalRpd) { return; }
    (this.orderedLegNumbers || []).forEach(n => this.getRpdLeg(n));
  }

  private ensureLiftPumpLegRows() {
    if (!this.finalValuesTemplate?.hasLiftPumpsUsed || !this.liftPumpsUsed) { return; }
    (this.orderedLegNumbers || []).forEach(n => this.getLiftPumpLeg(n));
  }

  // =====================================================================
  // TIME NORMALIZATION
  // =====================================================================

  private asDateTime(value: DateTime | PtLuxonDate | null | undefined): DateTime | null {
    if (!value) { return null; }

    const maybeDt = value as any;
    if (maybeDt?.isValid !== undefined && typeof maybeDt.toISO === 'function') {
      return value as DateTime;
    }

    const pt = PtLuxonDate.assignPtLuxonDate(value as PtLuxonDate);
    return pt?.dateTime || null;
  }

  private setBlockTimeFromPt(block: FinalTimeBlock, ptLocal: PtLuxonDate) {
    const dt = ptLocal?.dateTime || null;
    block.timeUtc = dt;
    block.timeOffset = dt ? (dt.offset / 60) : null;
  }

  private clearBlockTime(block: FinalTimeBlock) {
    block.timeUtc = null;
    block.timeOffset = null;
    if (block.timeConfirmed !== undefined) {
      block.timeConfirmed = false;
    }
  }

  // =====================================================================
  // HYDRATION (UI)
  // =====================================================================

  private hydrateAllTimes() {
    this.hydrateTimeBlock(
      'finalLegValues',
      this.finalLegValues,
      v => (this.finalLegTimeToShow = v),
      v => (this.finalLegSelectedLocalDateIso = v)
    );

    this.hydrateTimeBlock(
      'finalPenetrations',
      this.finalPenetrations,
      v => (this.finalPenTimeToShow = v),
      v => (this.finalPenSelectedLocalDateIso = v)
    );

    this.hydrateTimeBlock(
      'differenceFromExpected',
      this.differenceFromExpected,
      v => (this.diffTimeToShow = v),
      v => (this.diffSelectedLocalDateIso = v)
    );

    this.hydrateTimeBlock(
      'finalRpd',
      this.finalRpd as any,
      v => (this.finalRpdTimeToShow = v),
      v => (this.finalRpdSelectedLocalDateIso = v)
    );

    this.hydrateTimeBlock(
      'finalAirGap',
      this.finalAirGap,
      v => (this.finalAirGapTimeToShow = v),
      v => (this.finalAirGapSelectedLocalDateIso = v)
    );
  }

  private hydrateTimeBlock(
    _key: FinalTimeBlockKey,
    block: FinalTimeBlock | null,
    setTime: (v: string) => void,
    setIso: (v: string) => void
  ) {
    if (!block) {
      setTime('');
      setIso('');
      this.dtHelper.updateMask(this.mask, '');
      return;
    }

    const dt = this.asDateTime(block.timeUtc as any);
    if (!dt) {
      setTime('');
      setIso('');
      this.dtHelper.updateMask(this.mask, '');
      return;
    }

    const iso = dt.toISODate() || '';
    const t = dt.toFormat('HH:mm') || '';
    setIso(iso);
    setTime(t);
    this.dtHelper.updateMask(this.mask, t);
  }

  // =====================================================================
  // BASE DATE PICKING
  // =====================================================================

  private normalizeSelectedLocalDateIso(value: string | DateTime | PtLuxonDate | null | undefined): string | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
    }

    const dt = this.asDateTime(value as any);
    return dt?.toISODate?.() || null;
  }

  private getBaseDateFor(selectedLocalDateIso: string | DateTime | PtLuxonDate | null, fallbackIso: string | null): DateTime {
    const startUtc = this.editor?.startDate?.dateTime || DateTime.utc();
    const startOffsetMinutes = this.editor?.startOffset || 0;

    let iso: string | null = null;

    if (this.activitySpansMultipleDays) {
      iso = this.normalizeSelectedLocalDateIso(selectedLocalDateIso);
      iso = iso || fallbackIso || null;
    }

    return this.dtHelper.getBaseDateUtcMidnight(
      startUtc,
      startOffsetMinutes,
      this.activitySpansMultipleDays,
      iso
    );
  }

  // =====================================================================
  // PREVIOUS-TIME (TIMELINE) LOGIC
  // =====================================================================

  private getLatestEnteredEventDateTime(): DateTime | null {
    const rows: JackingEventRowEditor[] = this.editor?.jackingEvents?.eventRows || [];
    if (!rows.length) { return null; }

    let best: DateTime | null = null;

    rows.forEach(r => {
      if (!r?.requiresDateTime) { return; }
      if (!r?.eventDate) { return; }

      const pt = PtLuxonDate.assignPtLuxonDate(r.eventDate);
      const dt = pt?.dateTime || null;
      if (!dt) { return; }

      if (!best) {
        best = dt;
        return;
      }

      if (this.dateService.isFirstDateLargerOffsetsPreApplied(dt, best)) {
        best = dt;
      }
    });

    return best;
  }

  private getFinalBlockOrder(): Array<{ key: FinalTimeBlockKey; block: FinalTimeBlock | null }> {
    return [
      { key: 'finalLegValues', block: this.finalLegValues as any },
      { key: 'finalPenetrations', block: this.finalPenetrations as any },
      { key: 'differenceFromExpected', block: this.differenceFromExpected as any },
      { key: 'finalRpd', block: this.finalRpd as any },
      { key: 'finalAirGap', block: this.finalAirGap as any }
    ];
  }

  private getPreviousTimelineDateTime(currentKey: FinalTimeBlockKey): DateTime | null {
    const latestEvent = this.getLatestEnteredEventDateTime();

    const blocks = this.getFinalBlockOrder();
    const idx = blocks.findIndex(b => b.key === currentKey);

    let bestFinal: DateTime | null = null;
    for (let i = 0; i < idx; i++) {
      const b = blocks[i].block;
      if (!b) { continue; }
      const dt = this.asDateTime(b.timeUtc as any);
      if (!dt) { continue; }

      if (!bestFinal) {
        bestFinal = dt;
        continue;
      }

      if (this.dateService.isFirstDateLargerOffsetsPreApplied(dt, bestFinal)) {
        bestFinal = dt;
      }
    }

    if (!latestEvent) { return bestFinal; }
    if (!bestFinal) { return latestEvent; }

    return this.dateService.isFirstDateLargerOffsetsPreApplied(latestEvent, bestFinal) ? latestEvent : bestFinal;
  }

  private getSuggestedIsoForKey(currentKey: FinalTimeBlockKey): string | null {
    // If no selected date, default to the last timeline time (events or earlier finals)
    const prev = this.getPreviousTimelineDateTime(currentKey);
    return prev ? (prev.toISODate() || null) : null;
  }

  // =====================================================================
  // APPLY TIME (USER INPUT) + BUMP DAY
  // =====================================================================

  private async applyTimeWithBump(
    key: FinalTimeBlockKey,
    block: FinalTimeBlock | null,
    timeToShow: string,
    selectedLocalDateIso: string
  ) {
    if (this.isReadonly || !block) { return; }

    const t = (timeToShow || '').trim();
    if (!t) {
      this.clearBlockTime(block);
      this.syncFinalPts();
      return;
    }

    const fallbackIso = this.getSuggestedIsoForKey(key);
    const baseDate = this.getBaseDateFor(selectedLocalDateIso || null, fallbackIso);

    const ptLocal = await this.dtHelper.buildLocalPtFromTime(t, baseDate);
    if (!ptLocal) { return; }

    // if offset picker still needs a choice, keep the pt around for the picker
    if (ptLocal.offsetNull && ptLocal.offsetNull()) {
      block.timeUtc = ptLocal;
      this.syncFinalPts();
      return;
    }

    this.setBlockTimeFromPt(block, ptLocal);

    // bump if before previous timeline entry
    await this.bumpBlockIfBeforePrevious(key, block, t);

    this.syncFinalPts();
  }

  private async bumpBlockIfBeforePrevious(key: FinalTimeBlockKey, block: FinalTimeBlock, timeToShow: string) {
    const currDt = this.asDateTime(block.timeUtc as any);
    if (!currDt) { return; }

    const prevDt = this.getPreviousTimelineDateTime(key);
    if (!prevDt) { return; }

    // If prev > curr => bump by whole days until curr >= prev
    while (this.dateService.isFirstDateLargerOffsetsPreApplied(prevDt, currDt)) {
      const bumpedRaw = await this.dateService.incrementPtLuxonDateFromUserEnteredString(timeToShow, currDt);

      const bumpedPt = PtLuxonDate.assignPtLuxonDate(bumpedRaw as any);
      const bumpedDt = bumpedPt?.dateTime || null;
      if (!bumpedDt) { return; }

      block.timeUtc = bumpedDt;
      block.timeOffset = bumpedDt.offset / 60;

      // update loop variable
      (block as any).__tmpCurr = bumpedDt; // set to avoid re-creating vars in a tight loop
      (currDt as any) = bumpedDt;

      const dt = this.asDateTime(block.timeUtc as any);
      if (dt) {
        await this.timeEngine.bumpEditorEndIfNeeded(this.editor, dt);
      }
      break; // incrementPtLuxonDateFromUserEnteredString should already bump one day; loop isn't normally needed
    }
  }

  // =====================================================================
  // FINAL LEG VALUES TIME HANDLERS
  // =====================================================================

  async onFinalLegDateChanged(dateIso: DateTime | string | PtLuxonDate) {
    this.finalLegSelectedLocalDateIso = this.normalizeSelectedLocalDateIso(dateIso) || '';
    await this.applyTimeWithBump('finalLegValues', this.finalLegValues as any, this.finalLegTimeToShow, this.finalLegSelectedLocalDateIso);
  }

  async onFinalLegTimeTextChanged(timeToShow: string) {
    this.finalLegTimeToShow = (timeToShow || '').trim();
    this.dtHelper.updateMask(this.mask, this.finalLegTimeToShow);
    await this.applyTimeWithBump('finalLegValues', this.finalLegValues as any, this.finalLegTimeToShow, this.finalLegSelectedLocalDateIso);
  }

  onFinalLegOffsetSelected(ptLocal: PtLuxonDate) {
    if (this.isReadonly || !this.finalLegValues || !ptLocal?.dateTime) { return; }
    this.setBlockTimeFromPt(this.finalLegValues as any, PtLuxonDate.assignPtLuxonDate(ptLocal));
    this.syncFinalPts();
  }

  // =====================================================================
  // FINAL PENETRATIONS TIME HANDLERS
  // =====================================================================

  async onFinalPenDateChanged(dateIso: DateTime | string | PtLuxonDate) {
    this.finalPenSelectedLocalDateIso = this.normalizeSelectedLocalDateIso(dateIso) || '';
    await this.applyTimeWithBump('finalPenetrations', this.finalPenetrations as any, this.finalPenTimeToShow, this.finalPenSelectedLocalDateIso);
  }

  async onFinalPenTimeTextChanged(timeToShow: string) {
    this.finalPenTimeToShow = (timeToShow || '').trim();
    this.dtHelper.updateMask(this.mask, this.finalPenTimeToShow);
    await this.applyTimeWithBump('finalPenetrations', this.finalPenetrations as any, this.finalPenTimeToShow, this.finalPenSelectedLocalDateIso);
  }

  onFinalPenOffsetSelected(ptLocal: PtLuxonDate) {
    if (this.isReadonly || !this.finalPenetrations || !ptLocal?.dateTime) { return; }
    this.setBlockTimeFromPt(this.finalPenetrations as any, PtLuxonDate.assignPtLuxonDate(ptLocal));
    this.syncFinalPts();
  }

  // =====================================================================
  // DIFFERENCE TIME HANDLERS
  // =====================================================================

  async onDiffDateChanged(dateIso: DateTime | string | PtLuxonDate) {
    this.diffSelectedLocalDateIso = this.normalizeSelectedLocalDateIso(dateIso) || '';
    await this.applyTimeWithBump('differenceFromExpected', this.differenceFromExpected as any, this.diffTimeToShow, this.diffSelectedLocalDateIso);
  }

  async onDiffTimeTextChanged(timeToShow: string) {
    this.diffTimeToShow = (timeToShow || '').trim();
    this.dtHelper.updateMask(this.mask, this.diffTimeToShow);
    await this.applyTimeWithBump('differenceFromExpected', this.differenceFromExpected as any, this.diffTimeToShow, this.diffSelectedLocalDateIso);
  }

  onDiffOffsetSelected(ptLocal: PtLuxonDate) {
    if (this.isReadonly || !this.differenceFromExpected || !ptLocal?.dateTime) { return; }
    this.setBlockTimeFromPt(this.differenceFromExpected as any, PtLuxonDate.assignPtLuxonDate(ptLocal));
    this.syncFinalPts();
  }

  // =====================================================================
  // FINAL RPD TIME HANDLERS
  // =====================================================================

  async onFinalRpdDateChanged(dateIso: DateTime | string | PtLuxonDate) {
    this.finalRpdSelectedLocalDateIso = this.normalizeSelectedLocalDateIso(dateIso) || '';
    await this.applyTimeWithBump('finalRpd', this.finalRpd as any, this.finalRpdTimeToShow, this.finalRpdSelectedLocalDateIso);
  }

  async onFinalRpdTimeTextChanged(timeToShow: string) {
    this.finalRpdTimeToShow = (timeToShow || '').trim();
    this.dtHelper.updateMask(this.mask, this.finalRpdTimeToShow);
    await this.applyTimeWithBump('finalRpd', this.finalRpd as any, this.finalRpdTimeToShow, this.finalRpdSelectedLocalDateIso);
  }

  onFinalRpdOffsetSelected(ptLocal: PtLuxonDate) {
    if (this.isReadonly || !this.finalRpd || !ptLocal?.dateTime) { return; }
    this.setBlockTimeFromPt(this.finalRpd as any, PtLuxonDate.assignPtLuxonDate(ptLocal));
    this.syncFinalPts();
  }

  // =====================================================================
  // FINAL AIR GAP TIME HANDLERS
  // =====================================================================

  async onFinalAirGapDateChanged(dateIso: DateTime | string | PtLuxonDate) {
    this.finalAirGapSelectedLocalDateIso = this.normalizeSelectedLocalDateIso(dateIso) || '';
    await this.applyTimeWithBump('finalAirGap', this.finalAirGap as any, this.finalAirGapTimeToShow, this.finalAirGapSelectedLocalDateIso);
    this.applyOperationEndLink();
  }

  async onFinalAirGapTimeTextChanged(timeToShow: string) {
    this.finalAirGapTimeToShow = (timeToShow || '').trim();
    this.dtHelper.updateMask(this.mask, this.finalAirGapTimeToShow);
    await this.applyTimeWithBump('finalAirGap', this.finalAirGap as any, this.finalAirGapTimeToShow, this.finalAirGapSelectedLocalDateIso);
    this.applyOperationEndLink();
  }

  onFinalAirGapOffsetSelected(ptLocal: PtLuxonDate) {
    if (this.isReadonly || !this.finalAirGap || !ptLocal?.dateTime) { return; }
    this.setBlockTimeFromPt(this.finalAirGap as any, PtLuxonDate.assignPtLuxonDate(ptLocal));
    this.syncFinalPts();
  }

  // =====================================================================
  // RPD VALIDATION (unchanged)
  // =====================================================================

  private parseNullableNumber(v: any): number | null {
    if (v === null || v === undefined) { return null; }
    const s = (typeof v === 'string') ? v.trim() : v;
    if (s === '') { return null; }
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  private isRpdTripletComplete(leg: any): boolean {
    const r = this.parseNullableNumber(leg?.r);
    const p = this.parseNullableNumber(leg?.p);
    const d = this.parseNullableNumber(leg?.d);
    return r !== null && p !== null && d !== null;
  }

  public isRpdLegValid(legNo: number): boolean {
    if (!this.finalValuesTemplate?.hasRpd) {
      return true;
    }

    const leg = this.getRpdLeg(legNo);

    // don't nag until all 3 are entered
    if (!this.isRpdTripletComplete(leg)) {
      return true;
    }

    const r = this.parseNullableNumber(leg?.r);
    const p = this.parseNullableNumber(leg?.p);
    const d = this.parseNullableNumber(leg?.d);

    return r === 0 || p === 0 || d === 0;
  }

  public getInvalidRpdLegNumbers(): number[] {
    if (!this.finalValuesTemplate?.hasRpd) {
      return [];
    }

    return (this.orderedLegNumbers || []).filter(legNo => !this.isRpdLegValid(legNo));
  }

  public showRpdRuleMessage(): boolean {
    return this.getInvalidRpdLegNumbers().length > 0;
  }

  public formatLegList(legs: number[]): string {
    return (legs || []).join(', ');
  }

  // =====================================================================
  // PT WRAPPER SYNC (FOR DATE-TIME CELL INPUTS)
  // =====================================================================

  private syncFinalPts() {
    // These are the objects bound into <jacking-date-time-cell [ptLuxonDate]="...">
    // That component expects a PtLuxonDate object; we only need dateTime populated.
    this.finalLegPt.dateTime = this.asDateTime(this.finalLegValues?.timeUtc as any) as any;
    this.finalPenPt.dateTime = this.asDateTime(this.finalPenetrations?.timeUtc as any) as any;
    this.diffPt.dateTime = this.asDateTime(this.differenceFromExpected?.timeUtc as any) as any;
    this.finalRpdPt.dateTime = this.asDateTime((this.finalRpd as any)?.timeUtc as any) as any;
    this.finalAirGapPt.dateTime = this.asDateTime(this.finalAirGap?.timeUtc as any) as any;
  }

  private applyOperationEndLink(): void {
    if (!this.editor
      || !this.finalAirGapPt?.dateTime
      || !this.finalAirGapTimeToShow
      || this.finalAirGapTimeToShow.endsWith('_')
      || this.editor.formTypeId !== 'arrival') {
      return;
    }

    this.editor.endDate = this.finalAirGapPt;

    const dtLocal =  this.finalAirGapPt.dateTime;
    const offsetHours = dtLocal.offset / 60;
    const offsetLabel = `(${offsetHours >= 0 ? '+' : ''}${offsetHours})`;

    this.editor.dataByKey = this.editor.dataByKey || {};
    this.editor.dataByKey.EndDate = `${dtLocal.toFormat('dd/LL/yyyy, HH:mm')} ${offsetLabel}`;
  }

  private getFocusZone(target: HTMLElement): 'item' | 'time' | 'leg' | 'other' {
    if (target.closest('.leg-block') || target.closest('.cell--alllegs')) {
      return 'leg';
    }

    if (target.closest('.cell--time')) {
      return 'time';
    }

    if (target.closest('.cell--item')) {
      return 'item';
    }

    return 'other';
  }

  private resetHorizontalScroll(): void {
    const scrollEl = this.finalValuesScrollRef?.nativeElement;
    if (!scrollEl) {
      return;
    }

    window.requestAnimationFrame(() => {
      scrollEl.scrollLeft = 0;
    });
  }

  private moveFromTimeInputToRowValues(event: KeyboardEvent): boolean {
    const activeElement = event.target as HTMLElement | null;
    if (!activeElement || !activeElement.classList.contains('time-input')) {
      return false;
    }

    const row = activeElement.closest('.final-row.body') as HTMLElement | null;
    if (!row) {
      return false;
    }

    const firstValueField = row.querySelector<HTMLElement>(
      '.leg-block input:not([disabled]), .leg-block select:not([disabled]), .leg-block textarea:not([disabled]), .leg-block button:not([disabled]), .leg-block [tabindex]:not([tabindex="-1"]), ' +
      '.cell--alllegs input:not([disabled]), .cell--alllegs select:not([disabled]), .cell--alllegs textarea:not([disabled]), .cell--alllegs button:not([disabled]), .cell--alllegs [tabindex]:not([tabindex="-1"])'
    );

    if (!firstValueField) {
      return false;
    }

    event.preventDefault();
    firstValueField.focus();
    return true;
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/components/jacking-events/jacking-final-values/jacking-final-values-grid/jacking-final-values-grid.component.ts